// subtaskMove — the rules for moving work-list items to another job.
//
// The last ❌ on the Jobs row of docs/REBUILD_PARITY.md. A crew runs out of
// daylight, and the leftovers have to become somebody's Tuesday. The classic
// Jobs screen can do it; the rebuild could not, so the only options were
// retyping the items on a new job or leaving them on a job marked finished.
//
// Every rule below is read off server/routes/subtasks.js POST /move rather
// than assumed, because four of them are invisible from the endpoint's name:
//
//   body   { subtask_ids[≥1], source_job_id, target_job_id?, new_job_title? }
//   role   admin or manager ONLY — anyone else is 403
//   target no target_job_id → new_job_title is REQUIRED (else 400), and a
//          continuation job is created carrying the source job's user_id,
//          client_id and property_id
//   scope  the UPDATE is `WHERE id = ? AND job_id = ? AND completed = 0`, so a
//          COMPLETED item silently does not move and is not counted
//   answer { message, moved_count, target_job_id, job_title }
//
// The `completed = 0` clause is the one that decides the whole UI. A ticked
// item cannot move, so offering it would produce a sheet where five items are
// picked, three arrive, and nothing says which two did not. Offer only what
// can actually go, and report what actually went.

/**
 * The two roles that hold the permission by seed. Kept because the divergence
 * suite reads it, and because it names what "oversight" resolves to today —
 * but it is no longer what the check consults. See below.
 */
export const MOVE_ROLES = ['admin', 'manager'];

/**
 * May this person move items at all?
 *
 * Checked before the button is rendered rather than after the refusal,
 * because a control that always 403s teaches people the app is broken.
 *
 * This used to compare the ROLE LABEL, and said so: "extra_permissions does
 * not open this handler, so it must not be consulted here either." That was
 * true and stopped being true on 2026-09-07, when POST /api/subtasks/move
 * moved to `jobs.manage_all || jobs.manage_team`. The comment outliving the
 * code is exactly the failure docs/AGENT_LEARNINGS.md warns about — a note
 * that has quietly become false is worse than no note, because it gets
 * followed.
 *
 * The direction of the old bug was safe (a granted person saw no button for
 * something the server would have allowed — under-offering, not a ghost
 * button), which is why it was a defect and not an incident. It is still a
 * defect: granting somebody a permission and having the UI ignore it is the
 * thing the owner asked to stop happening.
 *
 * Mirrors `hasJobOversight` in server/routes/subtasks.js exactly:
 *   admin short-circuit · jobs.manage_all · jobs.manage_team
 * `manage_team` is checked explicitly because it is a MARKER, not a wildcard —
 * only `manage_all` widens (see client/src/hooks/usePermission.js).
 *
 * Takes the user object, not a role string, because a role string cannot
 * carry a grant. A bare string returns false rather than guessing.
 */
export function canMoveSubtasks(user) {
  if (!user || typeof user !== 'object') return false;
  if (user.role === 'admin') return true;
  const held = (user.permissions && user.permissions.jobs) || [];
  return held.includes('manage_all') || held.includes('manage_team');
}

/** Positive integers only, deduped, order preserved. */
function cleanIds(ids) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(ids) ? ids : []) {
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/**
 * The items that can actually move: the unticked ones.
 *
 * Not a cosmetic filter — it mirrors `AND completed = 0` in the handler's
 * UPDATE. A ticked item offered here is an item the person will watch fail to
 * move with no explanation.
 */
export function movableSubtasks(rows) {
  return (Array.isArray(rows) ? rows : []).filter((s) => s && !s.completed);
}

/**
 * The refusal to show BEFORE sending, in the same terms the server would use.
 * `null` means the request is worth making.
 */
export function validateMove({ subtaskIds, sourceJobId, mode, targetJobId, newJobTitle } = {}) {
  if (!cleanIds(subtaskIds).length) return 'Pick at least one item to move.';
  if (mode === 'new') {
    // The handler answers 400 "New job title is required" for this exact case.
    if (!String(newJobTitle || '').trim()) return 'Name the job these items move to.';
    return null;
  }
  if (!targetJobId) return 'Pick the job these items move to.';
  // The server would happily "move" an item onto the job it is already on —
  // changes > 0, counted as moved, nothing actually different. Refuse here so
  // nobody is told five items moved when the board looks identical.
  if (Number(targetJobId) === Number(sourceJobId)) return 'Those items are already on that job.';
  return null;
}

/** The request body, in the shape the validators accept. */
export function moveBody({ subtaskIds, sourceJobId, mode, targetJobId, newJobTitle } = {}) {
  const base = { subtask_ids: cleanIds(subtaskIds), source_job_id: Number(sourceJobId) };
  if (mode === 'new') {
    return { ...base, target_job_id: null, new_job_title: String(newJobTitle || '').trim() };
  }
  return { ...base, target_job_id: Number(targetJobId) };
}

/**
 * Jobs worth offering as a destination: everything except this one.
 *
 * Deliberately does NOT filter by status. Moving leftovers onto a job that is
 * already marked completed is unusual but not wrong — a lead reopening
 * yesterday's job is exactly the case this feature exists for — and the server
 * does not refuse it, so neither does the picker.
 */
export function moveTargets(jobs, sourceJobId) {
  return (Array.isArray(jobs) ? jobs : []).filter((j) => j && Number(j.id) !== Number(sourceJobId));
}

/**
 * What actually happened, said honestly.
 *
 * `moved_count` can be LESS than the number picked, because anything ticked
 * between opening the sheet and confirming it silently stays put. Echoing the
 * requested number back would be a claim about work that did not move — the
 * shape issue #465 was filed for, one collection over.
 *
 * A missing count is not zero: an answer whose `moved_count` did not arrive
 * says so rather than reporting nothing moved, because the two call for
 * opposite next actions.
 */
export function describeMoved(result, requested) {
  const raw = result && result.moved_count;
  const moved = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  const title = result && result.job_title;
  const where = title ? ` to "${title}"` : '';

  if (moved === null) return `Moved${where}, but the server did not say how many.`;
  if (moved === 0) return `Nothing moved${where}. Ticked items stay where they are.`;

  const asked = Number(requested);
  if (Number.isFinite(asked) && asked > moved) {
    return `Moved ${moved} of ${asked}${where}. The rest were ticked off, and ticked items do not move.`;
  }
  return `Moved ${moved} ${moved === 1 ? 'item' : 'items'}${where}.`;
}
